/**
 * Background GPS Hook
 * Automatically captures GPS location in the background without user interface
 * Updates location every 10 seconds when active
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { GPSCoordinates } from '../lib/gpsUtils';

interface BackgroundGPSOptions {
  autoStart?: boolean;
  updateInterval?: number; // in milliseconds
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

interface BackgroundGPSState {
  coordinates: GPSCoordinates | null;
  isActive: boolean;
  lastUpdated: Date | null;
  error: string | null;
  accuracy: number | null;
}

export const useBackgroundGPS = (options: BackgroundGPSOptions = {}) => {
  const {
    autoStart = false,
    updateInterval = 10000, // 10 seconds
    enableHighAccuracy = true,
    timeout = 10000,
    maximumAge = 60000
  } = options;

  const [state, setState] = useState<BackgroundGPSState>({
    coordinates: null,
    isActive: false,
    lastUpdated: null,
    error: null,
    accuracy: null
  });

  const watchIdRef = useRef<number | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const geolocationOptions: PositionOptions = {
    enableHighAccuracy,
    timeout,
    maximumAge
  };

  const updateLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setState(prev => ({
        ...prev,
        error: 'Geolocation is not supported by this browser',
        isActive: false
      }));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coordinates: GPSCoordinates = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        setState(prev => ({
          ...prev,
          coordinates,
          lastUpdated: new Date(),
          error: null,
          accuracy: position.coords.accuracy
        }));

      },
      (error) => {
        let errorMessage = 'Failed to get location';
        
        switch (error.code) {
          case 1: // GeolocationPositionError.PERMISSION_DENIED
            errorMessage = 'Location permission denied';
            break;
          case 2: // GeolocationPositionError.POSITION_UNAVAILABLE
            errorMessage = 'Location information unavailable';
            break;
          case 3: // GeolocationPositionError.TIMEOUT
            errorMessage = 'Location request timed out';
            break;
        }

        setState(prev => ({
          ...prev,
          error: errorMessage
        }));

      },
      geolocationOptions
    );
  }, [enableHighAccuracy, timeout, maximumAge]);

  const startTracking = useCallback(() => {
    if (state.isActive) return;

    
    setState(prev => ({ ...prev, isActive: true, error: null }));

    // Get initial location immediately
    updateLocation();

    // Set up interval to update every 10 seconds
    intervalIdRef.current = setInterval(updateLocation, updateInterval);
  }, [state.isActive, updateLocation, updateInterval]);

  const stopTracking = useCallback(() => {
    if (!state.isActive) return;


    setState(prev => ({ ...prev, isActive: false }));

    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, [state.isActive]);

  const resetLocation = useCallback(() => {
    setState(prev => ({
      ...prev,
      coordinates: null,
      lastUpdated: null,
      error: null,
      accuracy: null
    }));
  }, []);

  // Auto-start if enabled
  useEffect(() => {
    if (autoStart && !state.isActive) {
      startTracking();
    }

    return () => {
      stopTracking();
    };
  }, [autoStart]); // Only run on mount/unmount

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (intervalIdRef.current !== null) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, []);

  return {
    // Current state
    coordinates: state.coordinates,
    isActive: state.isActive,
    lastUpdated: state.lastUpdated,
    error: state.error,
    accuracy: state.accuracy,
    
    // Actions
    startTracking,
    stopTracking,
    resetLocation,
    updateLocation,
    
    // Status helpers
    hasLocation: !!state.coordinates,
    isLocationStale: state.lastUpdated 
      ? (Date.now() - state.lastUpdated.getTime()) > 20000 // 20 seconds
      : false
  };
};